import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useReviewPriorityRules(workspaceId: string) {
  return useQuery({
    queryKey: ['review-priority-rules', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_priority_rules')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('priority_score', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useCreatePriorityRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: any) => {
      const { data, error } = await supabase
        .from('review_priority_rules')
        .insert(rule)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-priority-rules'] });
      toast.success('Priority rule created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create rule: ' + error.message);
    }
  });
}

export function useUpdatePriorityRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { data, error } = await supabase
        .from('review_priority_rules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-priority-rules'] });
      toast.success('Priority rule updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update rule: ' + error.message);
    }
  });
}

export function useCalculatePriority() {
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      queueItemId: string;
      confidence?: number;
      amount?: number;
      vendorId?: string;
      slaDueAt?: string;
      workspaceId: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('calculate-review-priority', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onError: (error: Error) => {
      toast.error('Failed to calculate priority: ' + error.message);
    }
  });
}
